import React, { useRef, useEffect } from "react";
import { Bold, Italic, List, ListOrdered, Link2 } from "lucide-react";

// Minimal HTML sanitizer for admin-authored rich text. Allows only a small tag
// whitelist (bold/italic/lists/links/paragraphs/breaks) and safe href schemes.
const ALLOWED = new Set(["P", "BR", "B", "STRONG", "I", "EM", "U", "UL", "OL", "LI", "A"]);
export function sanitizeHtml(html) {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const root = doc.body.firstChild;
  const walk = (node) => {
    [...node.childNodes].forEach((child) => {
      if (child.nodeType === 3) return; // text
      if (child.nodeType !== 1 || !ALLOWED.has(child.tagName)) {
        // unwrap disallowed element, keep its text/children
        while (child.firstChild) node.insertBefore(child.firstChild, child);
        node.removeChild(child);
        return;
      }
      // strip all attributes except safe href on <a>
      [...child.attributes].forEach((a) => {
        if (child.tagName === "A" && a.name === "href" && /^(https?:|mailto:|\/|#)/i.test(a.value)) return;
        child.removeAttribute(a.name);
      });
      if (child.tagName === "A") { child.setAttribute("target", "_blank"); child.setAttribute("rel", "noreferrer"); }
      walk(child);
    });
  };
  walk(root);
  return root.innerHTML;
}

// Renders sanitized rich-text HTML on the public site. Uses the shared prose styling.
export function RichContent({ html, className = "" }) {
  return <div className={`rich-content ${className}`} dangerouslySetInnerHTML={{ __html: sanitizeHtml(html || "") }} />;
}

// contentEditable rich-text editor for the admin (bold / italic / lists / link only).
export function RichText({ value, onChange, testid }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
  }, [value]);

  const exec = (cmd) => {
    ref.current?.focus();
    document.execCommand(cmd, false, null);
    emit();
  };
  const addLink = () => {
    const url = window.prompt("Link URL (https://…, mailto:…, or /page)");
    if (!url) return;
    if (!/^(https?:|mailto:|\/|#)/i.test(url)) { window.alert("Please enter a valid URL."); return; }
    ref.current?.focus();
    document.execCommand("createLink", false, url);
    emit();
  };
  const emit = () => onChange(sanitizeHtml(ref.current?.innerHTML || ""));

  const Btn = ({ onClick, children, label }) => (
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} title={label} aria-label={label}
      className="w-8 h-8 flex items-center justify-center border border-[#E4E1DB] bg-white hover:bg-[#F0EFEA] text-[#1C1B1A]">
      {children}
    </button>
  );

  return (
    <div className="border border-[#E4E1DB] bg-white" data-testid={testid}>
      <div className="flex gap-1 p-1.5 border-b border-[#E4E1DB]">
        <Btn onClick={() => exec("bold")} label="Bold"><Bold className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec("italic")} label="Italic"><Italic className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec("insertUnorderedList")} label="Bulleted list"><List className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec("insertOrderedList")} label="Numbered list"><ListOrdered className="w-4 h-4" /></Btn>
        <Btn onClick={addLink} label="Insert link"><Link2 className="w-4 h-4" /></Btn>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        className="rich-content min-h-[120px] px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-[#1C1B1A]"
      />
    </div>
  );
}
